import { processShowListingResponse } from './analyticsHelpers';

export async function hydrateTicketListing() {
	let hook = document.querySelector('[data-listing-hydrate]');
	if (!hook) {
		const results = document.querySelector('[data-results][data-total]');
		const bar = document.querySelector('[data-filterbar]');
		const endpoint = bar?.getAttribute('data-endpoint');
		if (!results || !endpoint) {
			return;
		}
		const total = parseInt(results.getAttribute('data-total') || '0', 10);
		const limit = parseInt(results.getAttribute('data-limit') || '24', 10);
		const offset = parseInt(results.getAttribute('data-offset') || String(limit), 10);
		const query = (() => {
			try {
				const stored = JSON.parse(results.getAttribute('data-query') || '{}');

				if (Object.keys(stored).length) {
					return stored;
				}

				/*
				 * data-query was not populated by the server-side render (or was
				 * set to an empty object). Fall back to the current URL search
				 * params so that paginated requests for page 2+ continue fetching
				 * the same filtered set rather than an unfiltered list.
				 */
				const fromUrl = {};
				new URLSearchParams(location.search).forEach((v, k) => {
					fromUrl[ k ] = v;
				});
				return fromUrl;
			} catch {
				return {};
			}
		})();
		if (total <= offset) {
			return;
		}
		const wrapper = document.querySelector('.shows-grid') || results;
		hook = document.createElement('div');
		hook.setAttribute('data-listing-hydrate', '');
		hook.setAttribute('data-endpoint', endpoint);
		hook.setAttribute('data-query', JSON.stringify(query));
		hook.setAttribute('data-offset', String(offset));
		hook.setAttribute('data-chunk', String(limit));
		hook.setAttribute('data-total', String(total));
		hook.setAttribute('data-mode', 'scroll');
		const sentinel = document.createElement('div'); sentinel.setAttribute('data-sentinel', ''); sentinel.style.height = '1px';
		wrapper.append(hook, sentinel);
	}

	const endpoint = hook.dataset.endpoint;
	const base = JSON.parse(hook.dataset.query || '{}');
	const initialOffset = parseInt(hook.dataset.offset || '0', 10);
	const chunk = parseInt(hook.dataset.chunk || '24', 10);
	const total = parseInt(hook.dataset.total || '0', 10);
	const mode = hook.dataset.mode || 'scroll'; // 'scroll' | 'one' | 'all'

	const results = document.querySelector('[data-results]');
	const sentinel = document.querySelector('[data-sentinel]');

	let loading = false;
	let done = false;
	let nextOffset = initialOffset;

	const setBusy = (flag) => results?.setAttribute('aria-busy', flag ? 'true' : 'false');

	const buildParams = (ofs) => {
		const p = new URLSearchParams();
		for (const [k, v] of Object.entries(base || {})) {
			if (v === null) {
				continue;
			}
			if (Array.isArray(v)) {
				v.forEach((val) => p.append(`${ k }[]`, String(val))); continue;
			}
			if (typeof v === 'boolean') {
				p.append(k, v ? '1' : '0'); continue;
			}
			const s = String(v).trim(); if (s !== '') {
				p.append(k, s);
			}
		}
		p.set('offset', String(ofs));
		p.set('limit', String(chunk));
		return p.toString();
	};

	async function load() {
		if (loading || done) {
			return;
		}
		loading = true; setBusy(true);

		try {
			const res = await fetch(`${ endpoint }?${ buildParams(nextOffset) }`, { credentials: 'same-origin' });
			if (!res.ok) {
				return;
			}

			const payload = await res.json();

			const html = (payload && typeof payload.html === 'string') ? payload.html : '';
			if (html) {
				results.insertAdjacentHTML('beforeend', html);
			}

			// Process analytics data (datalayer & structured data)
			// Don't clear previous data - we're appending to the existing results
			processShowListingResponse(payload, { clearPrevious: false });

			// Advance the offset using payload numbers if present, else safe fallbacks
			const got = Number.isFinite(payload?.limit) ? Number(payload.limit) : chunk;
			const off = Number.isFinite(payload?.offset) ? Number(payload.offset) : nextOffset;
			nextOffset = off + got;

			// Stop when we've loaded it all, or server sent no more HTML
			if (nextOffset >= total || !html.trim()) {
				done = true;
				sentinel?.remove();
				hook.remove();
			}
		} catch (err) {
			// Log and allow another intersection to retry
			console.error(err);
		} finally {
			loading = false; setBusy(false);
		}
	}

	if (mode === 'one') {
		(window.requestIdleCallback || setTimeout)(() => load(), 1);
		return;
	}

	if (mode === 'all') {
		const scheduleIdle = (cb) =>
			('requestIdleCallback' in window) ? window.requestIdleCallback(cb, { timeout: 250 }) : setTimeout(cb, 1);
		const loop = () => {
			if (!done) {
				load().then(() => !done && scheduleIdle(loop));
			}
		};
		scheduleIdle(loop);
		return;
	}

	// mode === 'scroll'
	if (!sentinel || typeof IntersectionObserver === 'undefined') {
		// Do nothing on load; you can wire a manual "Load more" button to call load() if you have one.
		return;
	}

	const startObserver = () => {
		if (!sentinel || done) {
			return;
		}
		const io = new IntersectionObserver((entries) => {
			for (const e of entries) {
				if (!e.isIntersecting) {
					continue;
				}
				io.unobserve(sentinel);
				load().finally(() => {
					if (!done) {
						io.observe(sentinel);
					}
				});
			}
		}, { rootMargin: '200px 0px', threshold: 0 });

		io.observe(sentinel);
	};

	// Only start observing after the first user scroll (prevents an immediate fetch on short pages)
	const hasScrolled = () => (window.scrollY || document.documentElement.scrollTop || 0) > 0;

	if (hasScrolled()) {
		startObserver();
	} else {
		const onFirstScroll = () => {
			window.removeEventListener('scroll', onFirstScroll);
			startObserver();
		};
		window.addEventListener('scroll', onFirstScroll, { once: true, passive: true });
	}
}